/**
 * 项目管理页面
 * 展示所有项目，支持搜索、筛选、查看详情
 */
import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ClockIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  SparklesIcon,
  UserGroupIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { ProjectInfo, ProjectType } from '../types';
import { projectApi, projectGroupApi, authApi } from '../services/api'; // 新增
import ConfirmDialog from '../components/ConfirmDialog';
import Toast, { ToastType } from '../components/Toast';
import LoadingOverlay from '../components/LoadingOverlay';

interface ProjectManagementProps {
  onEditProject?: (projectId: string, currentStep?: number) => void;
}

const ProjectManagement: React.FC<ProjectManagementProps> = ({ onEditProject }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState<ProjectType | ''>('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>(''); // 项目组过滤
  const [projectGroups, setProjectGroups] = useState<{id: string; name: string; is_default: boolean}[]>([]); // 项目组列表
  const [myProjectGroups, setMyProjectGroups] = useState<{id: string; name: string; is_default: boolean}[]>([]); // 我创建的项目组（复制用）
  const [projects, setProjects] = useState<ProjectInfo[]>([]); // 修改：从数据库加载
  const [loading, setLoading] = useState(true); // 新增：加载状态
  const [currentUser, setCurrentUser] = useState<{id: number; username: string; display_name: string} | null>(null); // 当前用户
  const [deleteTarget, setDeleteTarget] = useState<ProjectInfo | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null); // 复制中的项目id
  const [copyLoading, setCopyLoading] = useState({ show: false, message: '' }); // 复制加载状态
  const [deleteLoading, setDeleteLoading] = useState({ show: false, message: '' }); // 删除加载状态
  
  // 复制项目弹窗状态
  const [copyModal, setCopyModal] = useState<{
    show: boolean;
    project: ProjectInfo | null;
    newName: string;
    selectedGroupId: string;
  }>({ show: false, project: null, newName: '', selectedGroupId: '' });
  
  const [toast, setToast] = useState<{ show: boolean; type: ToastType; title: string; messages?: string[] }>({
    show: false,
    type: 'info',
    title: ''
  });

  const showToast = (type: ToastType, title: string, messages?: string[]) => {
    setToast({ show: true, type, title, messages });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  // 格式化Token数量（K/M格式）
  const formatTokens = (tokens: number = 0) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toLocaleString();
  };

  // 打开复制弹窗
  const openCopyModal = (project: ProjectInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    // 检查登录状态
    if (!currentUser) {
      showToast('warning', '请先登录', ['无法获取当前用户信息']);
      return;
    }
    // 找到默认项目组作为默认选中
    const defaultGroup = myProjectGroups.find((g) => g.is_default);
    const targetGroupId = defaultGroup?.id || (myProjectGroups[0]?.id ?? '');
    
    setCopyModal({
      show: true,
      project,
      newName: `${project.alias?.trim() || project.projectName} - 副本`,
      selectedGroupId: targetGroupId,
    });
  };

  // 执行复制
  const executeCopy = async () => {
    if (!copyModal.project) return;
    
    try {
      setCopyLoading({ show: true, message: '正在复制项目数据...' });
      setCopyingId(copyModal.project.id);
      
      // 调用后端API复制项目（传入新名称和项目组）
      const response = await projectApi.copyProject(copyModal.project.id, {
        alias: copyModal.newName,
        group_id: copyModal.selectedGroupId || undefined,
      });
      
      // 关闭弹窗
      setCopyModal({ show: false, project: null, newName: '', selectedGroupId: '' });
      
      // 重新加载项目列表
      const projectsResponse = await projectApi.getProjects();
      const projectList: ProjectInfo[] = projectsResponse.data.map((p: any) => ({
        id: p.id,
        projectName: p.name,
        projectType: p.project_type || '数据类',
        wordCount: p.word_count || 50000,
        companyName: p.company?.name || '未知主体',
        creator: p.user?.display_name || p.user?.username || '系统',
        userId: p.user_id,
        createdAt: new Date(p.created_at + 'Z').toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        updatedAt: new Date(p.updated_at + 'Z').toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        status: p.status || 'draft',
        currentStep: p.progress?.current_step || 0,
        maxReachedStep: p.progress?.max_reached_step || 0,
        tokenUsage: p.token_usage || 0,
        groupId: p.group_id || undefined,
      }));
      setProjects(projectList);
      
      setCopyLoading({ show: false, message: '' });
      showToast('success', '项目复制成功！', [`已从「${copyModal.project.projectName}」创建副本`]);
    } catch (error) {
      console.error('复制项目失败:', error);
      setCopyLoading({ show: false, message: '' });
      showToast('error', '复制项目失败', ['请稍后重试']);
    } finally {
      setCopyingId(null);
    }
  };


  // 从数据库加载项目列表
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // 获取当前用户信息
        try {
          const userRes = await authApi.getCurrentUser();
          setCurrentUser(userRes.data);
        } catch {
          // 未登录或获取失败
        }
        
        const [projectsRes, groupsRes] = await Promise.all([
          projectApi.getProjects(),
          projectGroupApi.getSimpleList(),
        ]);
        
        const groups = groupsRes.data || [];
        setProjectGroups(groups);
        
        // 只显示自己创建的项目组（owner）或默认分组
        const myGroups = groups.filter((g: any) => g.is_owner || g.my_role === 'owner' || g.is_default);
        setMyProjectGroups(myGroups);

        // 转换数据库格式为前端格式
        const projectList: ProjectInfo[] = projectsRes.data.map((p: any) => ({
          id: p.id,
          projectName: p.name,
          alias: p.alias || '',
          projectType: p.project_type || '数据类',
          wordCount: p.word_count || 50000,
          companyName: p.company?.name || '未知主体',
          creator: p.user?.display_name || p.user?.username || '系统',
          userId: p.user_id,
          createdAt: new Date(p.created_at + 'Z').toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          updatedAt: new Date(p.updated_at + 'Z').toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          status: p.status || 'draft',
          currentStep: p.progress?.current_step || 0,
          maxReachedStep: p.progress?.max_reached_step || 0,
          tokenUsage: p.token_usage || 0,
          groupId: p.group_id || undefined,
        }));
        
        setProjects(projectList);
      } catch (error) {
        console.error('加载项目列表失败:', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);

  // 监听全局刷新数据事件（登录成功后触发）
  useEffect(() => {
    const handleRefreshData = () => {
      console.log('[ProjectManagement] 收到刷新数据事件');
      // 重新加载数据
      const loadDataAfterLogin = async () => {
        setLoading(true);
        try {
          // 获取当前用户信息
          const userRes = await authApi.getCurrentUser();
          setCurrentUser(userRes.data);

          // 获取项目组列表
          const groupsRes = await projectGroupApi.getSimpleList();
          const groups = groupsRes.data.data || [];
          setProjectGroups(groups);

          // 获取项目列表
          const projectsRes = await projectApi.getProjects();
          
          // 只显示自己创建的项目组（owner）或默认分组
          const myGroups = groups.filter((g: any) => g.is_owner || g.my_role === 'owner' || g.is_default);
          setMyProjectGroups(myGroups);

          // 转换数据库格式为前端格式
          const projectList: ProjectInfo[] = projectsRes.data.map((p: any) => ({
            id: p.id,
            projectName: p.name,
            alias: p.alias || '',
            projectType: p.project_type || '数据类',
            wordCount: p.word_count || 50000,
            companyName: p.company?.name || '未知主体',
            creator: p.user?.display_name || p.user?.username || '系统',
            userId: p.user_id,
            createdAt: new Date(p.created_at + 'Z').toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            updatedAt: new Date(p.updated_at + 'Z').toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            status: p.status || 'draft',
            currentStep: p.progress?.current_step || 0,
            maxReachedStep: p.progress?.max_reached_step || 0,
            tokenUsage: p.token_usage || 0,
            groupId: p.group_id || undefined,
          }));
          
          setProjects(projectList);
        } catch (error) {
          console.error('刷新数据失败:', error);
        } finally {
          setLoading(false);
        }
      };
      
      loadDataAfterLogin();
    };

    window.addEventListener('app:refresh-data', handleRefreshData);
    return () => window.removeEventListener('app:refresh-data', handleRefreshData);
  }, []);

  const projectTypes: Array<ProjectType | ''> = ['', '通用类', '数据类', '定开类', '产品类', '两轮车'];

  // 按项目类型配置卡片与类型标签的配色
  const typeStyles: Record<ProjectType, { card: string; tag: string }> = {
    通用类: {
      card: 'from-gray-50 to-slate-50 border-gray-100',
      tag: 'bg-gray-100 text-gray-700 border-gray-200',
    },
    数据类: {
      card: 'from-sky-50 to-blue-50 border-sky-100',
      tag: 'bg-sky-100 text-sky-700 border-sky-200',
    },
    定开类: {
      card: 'from-emerald-50 to-teal-50 border-emerald-100',
      tag: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    },
    产品类: {
      card: 'from-violet-50 to-fuchsia-50 border-violet-100',
      tag: 'bg-violet-100 text-violet-700 border-violet-200',
    },
    两轮车: {
      card: 'from-amber-50 to-orange-50 border-amber-100',
      tag: 'bg-amber-100 text-amber-700 border-amber-200',
    },
  };

  const getTypeCardStyle = (type: ProjectType) => typeStyles[type].card;
  const getTypeTagStyle = (type: ProjectType) => typeStyles[type].tag;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading({ show: true, message: '正在删除项目...' });
      
      await projectApi.deleteProject(deleteTarget.id);
      setProjects((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      
      setDeleteLoading({ show: false, message: '' });
      setDeleteTarget(null);
      showToast('success', '项目删除成功！', [`已删除「${deleteTarget.projectName}」`]);
    } catch (error) {
      console.error('删除项目失败:', error);
      setDeleteLoading({ show: false, message: '' });
      showToast('error', '删除项目失败', ['请稍后重试']);
    }
  };
  // 筛选项目
  const filteredProjects = projects.filter((project) => {
    const matchSearch = 
      project.projectName.toLowerCase().includes(searchText.toLowerCase()) ||
      (project.alias && project.alias.toLowerCase().includes(searchText.toLowerCase())) ||
      project.companyName.toLowerCase().includes(searchText.toLowerCase()) ||
      project.creator.toLowerCase().includes(searchText.toLowerCase());
    
    const matchType = !selectedType || project.projectType === selectedType;
    const matchGroup = !selectedGroupId || project.groupId === selectedGroupId;
    
    return matchSearch && matchType && matchGroup;
  });

  return (
    <div className="h-full flex flex-col bg-transparent">
      {/* 复制加载蒙层 */}
      <LoadingOverlay show={copyLoading.show} message={copyLoading.message} />
      
      {/* 删除加载蒙层 */}
      <LoadingOverlay show={deleteLoading.show} message={deleteLoading.message} />
      
      {/* Toast 通知 */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        messages={toast.messages}
        onClose={closeToast}
        duration={3000}
      />
      
      {/* 头部区域 */}
      <div className="px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 text-center">项目管理</h1>

          {/* 搜索框 */}
          <div className="mt-5 relative max-w-2xl mx-auto">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索项目别名、项目名、主体、创建人..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-gray-50/60"
            />
          </div>

          {/* 类型 + 项目组筛选 */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-sm">
            {projectTypes.map((type) => {
              const isActive = selectedType === type;
              const label = type || '全部';
              return (
                <button
                  key={type || 'all'}
                  type="button"
                  onClick={() => setSelectedType(type)}
                  className={`px-3 py-1 rounded-full border text-xs md:text-sm transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                      : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              );
            })}
            {/* 项目组筛选：类型标签形式 */}
            {projectGroups.length > 0 && (
              <>
                <span className="text-gray-300 select-none">|</span>
                <button
                  type="button"
                  onClick={() => setSelectedGroupId('')}
                  className={`px-3 py-1 rounded-full border text-xs md:text-sm transition-colors flex items-center gap-1 ${
                    selectedGroupId === ''
                      ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                      : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100'
                  }`}
                >
                  <UserGroupIcon className="w-3.5 h-3.5" />全部组
                </button>
                {[...projectGroups]
                  .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                  .map(g => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setSelectedGroupId(g.id)}
                      className={`px-3 py-1 rounded-full border text-xs md:text-sm transition-colors flex items-center gap-1 ${
                        selectedGroupId === g.id
                          ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                          : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <UserGroupIcon className="w-3.5 h-3.5" />{g.name}
                    </button>
                  ))}
              </>
            )}
          </div>
        </div>

        {/* 统计信息：脱离居中容器，整体页面左对齐 */}
        <div className="mt-3 flex flex-wrap items-center justify-start gap-3 text-xs text-gray-500">
          <span>共 {filteredProjects.length} 个项目</span>
          <span>草稿 {projects.filter((p) => p.status === 'draft').length}</span>
          <span>进行中 {projects.filter((p) => p.status === 'in_progress').length}</span>
          <span>已完成 {projects.filter((p) => p.status === 'completed').length}</span>
        </div>

        {/* 渐变分割线 */}
        <div className="mt-3 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />
      </div>

      {/* 项目卡片列表 */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className={`group rounded-xl border bg-gradient-to-br ${getTypeCardStyle(
                project.projectType
              )} hover:shadow-md transition-all hover:-translate-y-0.5 relative`}
            >
              {/* 主/辅标签 - 卡片右上角 */}
              {currentUser && (
                <span className={`absolute top-0 right-0 text-[10px] font-bold text-white px-2 py-0.5 rounded-bl-lg ${
                  project.userId === currentUser.id
                    ? 'bg-blue-500'
                    : 'bg-gray-400'
                }`}>
                  {project.userId === currentUser.id ? '主' : '辅'}
                </span>
              )}
              <div className="p-4">
                {/* 标题与操作图标 */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 pr-6 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate" title={project.alias?.trim() || project.projectName}>
                      {project.alias?.trim() || project.projectName}
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-1" title={project.projectName}>
                      {project.alias?.trim() ? project.projectName : '\u00A0'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* 编辑按钮 */}
                    <button
                      className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditProject?.(project.id, project.currentStep);
                      }}
                      title="编辑项目"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    {/* 复制按钮 */}
                    <button
                      className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                      onClick={(e) => openCopyModal(project, e)}
                      disabled={copyingId === project.id}
                      title="复制项目"
                    >
                      {copyingId === project.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                      ) : (
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      )}
                    </button>
                    {/* 删除按钮 - 只有项目创建者才显示 */}
                    {currentUser && project.userId === currentUser.id && (
                      <button
                        className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(project);
                        }}
                        title="删除项目"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* 类型与步骤标签 */}
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-full bg-blue-50/70 text-blue-700 border border-blue-100 font-medium">
                    第 {(project.maxReachedStep || 0) + 1}/5 步
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full border ${getTypeTagStyle(
                      project.projectType
                    )}`}
                  >
                    {project.projectType}
                  </span>
                  {project.groupId && (() => {
                    const g = projectGroups.find(g => g.id === project.groupId);
                    return g ? (
                      <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100 flex items-center gap-0.5">
                        <UserGroupIcon className="w-3 h-3" />{g.name}
                      </span>
                    ) : null;
                  })()}
                </div>

                {/* 关键信息：两列展示 */}
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-gray-700">
                  <div className="flex items-center min-w-0">
                    <DocumentTextIcon className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{(project.wordCount / 10000).toFixed(1)}万字</span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <BuildingOfficeIcon className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{project.companyName}</span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <SparklesIcon className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{formatTokens(project.tokenUsage)} tokens</span>
                  </div>
                  <div className="flex items-center min-w-0">
                    <ClockIcon className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate text-[11px] text-gray-500">{project.createdAt}</span>
                  </div>
                </div>

                {/* 进度条 */}
                {project.status === 'in_progress' && (
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 bg-white/40 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                        style={{ width: `${((project.maxReachedStep || 0) / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-gray-600 font-medium">
                      {(project.maxReachedStep || 0) + 1}/5
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* 加载状态 */}
        {loading && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        )}

        {/* 空状态 */}
        {filteredProjects.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <FunnelIcon className="w-16 h-16 mb-4" />
            <p className="text-lg">暂无项目信息</p>
            <p className="text-sm mt-2">
              赶快去
              <button
                onClick={() => onEditProject?.('new')}
                className="text-blue-600 hover:text-blue-700 font-medium mx-1"
              >
                新建标书
              </button>
              吧
            </p>
          </div>
        )}
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="确认删除该项目？"
        message={deleteTarget ? `删除后将无法恢复。\n项目：${deleteTarget.projectName}` : ''}
        confirmText="删除"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* 复制项目弹窗 */}
      {copyModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">复制项目</h3>
            
            <div className="space-y-4">
              {/* 项目名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新项目名称</label>
                <input
                  type="text"
                  value={copyModal.newName}
                  onChange={(e) => setCopyModal(prev => ({ ...prev, newName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="请输入项目名称"
                />
              </div>
              
              {/* 项目组选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">所属项目组</label>
                <select
                  value={copyModal.selectedGroupId}
                  onChange={(e) => setCopyModal(prev => ({ ...prev, selectedGroupId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  {[...myProjectGroups]
                    .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
                    .map(g => (
                      <option key={g.id} value={g.id}>
                        {g.name}{g.is_default ? '（默认）' : ''}
                      </option>
                    ))}
                </select>
                {myProjectGroups.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">您还没有创建任何项目组</p>
                )}
              </div>
            </div>
            
            {/* 按钮 */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setCopyModal({ show: false, project: null, newName: '', selectedGroupId: '' })}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={executeCopy}
                disabled={!copyModal.newName.trim() || copyingId === copyModal.project?.id}
                className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                {copyingId === copyModal.project?.id ? '复制中...' : '确认复制'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
